import React, { useEffect, useState } from "react";
import {
  Inventory2Outlined,
  RestartAltOutlined,
  ExpandMoreOutlined,
  SaveOutlined,
  StraightenOutlined,
  PercentOutlined,
  CalculateOutlined,
} from "@mui/icons-material";
import { BASE_URL } from "../../constants";
import { toast } from "react-toastify";

const useViewportWidth = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    let frame = null;

    const handleResize = () => {
      if (frame) cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        setWidth(window.innerWidth);
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return width;
};

const FS = {
  xxs: "clamp(9px, 0.62vw, 11px)",
  xs: "clamp(10px, 0.72vw, 12px)",
  sm: "clamp(11px, 0.85vw, 13px)",
  base: "clamp(12px, 0.95vw, 15px)",
  md: "clamp(13px, 1.1vw, 16px)",
  lg: "clamp(15px, 1.3vw, 19px)",
  xl: "clamp(18px, 1.8vw, 25px)",
};

const ICON = {
  xs: "clamp(14px, 1.1vw, 18px)",
  sm: "clamp(16px, 1.3vw, 21px)",
  md: "clamp(18px, 1.5vw, 23px)",
  lg: "clamp(20px, 1.8vw, 26px)",
  xl: "clamp(28px, 2.6vw, 40px)",
};

const BOX = {
  tileSm: "clamp(28px, 2.3vw, 37px)",
  tileMd: "clamp(34px, 2.8vw, 46px)",

  inputH: "clamp(34px, 2.9vw, 44px)",
  inputSm: "clamp(32px, 2.7vw, 41px)",
  btnH: "clamp(34px, 2.9vw, 44px)",
  segH: "clamp(28px, 2.4vw, 36px)",

  radio: "clamp(11px, 0.95vw, 15px)",
};

const PAD = {
  xs: "clamp(6px, 0.6vw, 9px)",
  sm: "clamp(8px, 0.8vw, 12px)",
  md: "clamp(11px, 1.1vw, 17px)",
  lg: "clamp(14px, 1.5vw, 22px)",
};

const GAP = {
  xs: "clamp(5px, 0.5vw, 8px)",
  sm: "clamp(7px, 0.7vw, 11px)",
  md: "clamp(9px, 1vw, 14px)",
};

const AddProduct = ({
  setProducts,
  setSelectedProduct,
  cardStyle,
  labelStyle,
  inputStyle,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < 640;
  const isNarrow = viewportWidth < 900;
  const unitGridColumns = isNarrow ? "minmax(0, 1fr)" : "1fr 1fr";
  const row2Columns = isMobile
    ? "minmax(0, 1fr)"
    : isNarrow
      ? "repeat(2, minmax(0, 1fr))"
      : "repeat(3, minmax(0, 1fr))";

  const THEME = {
    primary: "#6D28D9",
    primaryDark: "#5B21B6",
    accent: "#8B5CF6",

    light: "#F5F3FF",
    lighter: "#FAF9FF",

    border: "#DDD6FE",
    iconBg: "#EDE9FE",

    text: "#334155",
    muted: "#64748B",
    lightText: "#94A3B8",
  };

  const [formData, setFormData] = useState({
    productName: "",
    unit1: "",
    unit2: "",
    conversionQty: "",
    primaryUnit: "unit1",
    tax: "",
    decimal: "No",
    tests: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      if (name === "unit2") {
        if (!value.trim()) {
          updated.conversionQty = "1";
          updated.primaryUnit = "unit1";
        } else if (prev.unit2.trim() === "") {
          /*
           * Unit 2 has just been entered.
           */
          updated.conversionQty = "";
        }
      }

      return updated;
    });
  };

  const handleReset = () => {
    setFormData({
      productName: "",
      unit1: "",
      unit2: "",
      conversionQty: "",
      primaryUnit: "unit1",
      tax: "",
      decimal: "No",
      tests: [],
    });
  };

  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: formData.productName.trim(),
        unit1: formData.unit1.trim(),
        unit2: formData.unit2?.trim() || null,
        conversionQty:
          formData.unit2?.trim() && formData.conversionQty
            ? Number(formData.conversionQty)
            : 1,
        primaryUnit: formData.primaryUnit || "unit1",
        tax: Number(formData.tax || 0),
        allowDecimal: formData.decimal === "Yes",
      };

      const response = await fetch(`${BASE_URL}api/user/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        toast.error(data.message || "Failed to create product");
        return;
      }

      const newProduct = data.data;
      setProducts((prev) => [newProduct, ...prev]);
      setSelectedProduct(newProduct);
      handleReset();
      setFormOpen(false);
      toast.success(data.message || "Product created successfully");
    } catch (error) {
      console.error("Create product error:", error);
      toast.error(error.message || "Unable to save product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      productName: product.productName || "",
      unit1: product.unit1 || "",
      unit2: product.unit2 || "",
      conversionQty: product.conversionQty || "",
      primaryUnit: product.primaryUnit || "unit1",
      tax: product.tax || "",
      decimal: product.decimal || "No",
      tests: product.tests || [],
    });

    setFormOpen(true);
  };

  return (
    <div
      style={{
        ...cardStyle,
        overflow: "hidden",
        background: "#FFFFFF",
      }}
    >
      {/* ======================================================
          ACCORDION HEADER
      ======================================================= */}

      <button
        type="button"
        onClick={() => setFormOpen(!formOpen)}
        style={{
          width: "100%",
          minHeight: "clamp(50px, 4.4vw, 68px)",

          border: "none",
          borderBottom: formOpen ? `1px solid ${THEME.border}` : "none",

          background: formOpen ? THEME.lighter : "#FFFFFF",

          padding: `${PAD.xs} ${PAD.md}`,

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: GAP.sm,

          cursor: "pointer",
          textAlign: "left",

          transition: "all 0.15s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: GAP.sm,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: BOX.tileMd,
              height: BOX.tileMd,
              flexShrink: 0,

              borderRadius: "9px",

              background: THEME.iconBg,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Inventory2Outlined
              style={{
                fontSize: ICON.lg,
                color: THEME.primary,
              }}
            />
          </div>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: FS.md,
                fontWeight: 700,
                color: "#1E293B",
              }}
            >
              Add Product
            </div>

            {!isMobile && (
              <div
                style={{
                  marginTop: "2px",
                  fontSize: FS.xs,
                  color: THEME.lightText,
                }}
              >
                Create and configure a new product
              </div>
            )}
          </div>
        </div>

        <ExpandMoreOutlined
          style={{
            color: THEME.muted,

            transform: formOpen ? "rotate(180deg)" : "rotate(0deg)",

            transition: "0.2s",

            fontSize: ICON.md,

            flexShrink: 0,
          }}
        />
      </button>

      {/* =================================================
          FORM
      ================================================== */}

      {formOpen && (
        <div
          style={{
            padding: PAD.md,
            background: "#FFFFFF",
          }}
        >
          <form onSubmit={handleSubmit}>
            {/* =====================================================
                PRODUCT NAME
            ====================================================== */}

            <div
              style={{
                marginBottom: PAD.sm,
              }}
            >
              <label
                style={{
                  ...labelStyle,

                  fontSize: FS.xs,
                  fontWeight: 700,
                  color: THEME.text,

                  marginBottom: "5px",
                  display: "block",
                }}
              >
                Product Name{" "}
                <span
                  style={{
                    color: "#DC2626",
                  }}
                >
                  *
                </span>
              </label>

              <input
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter product name"
                required
                style={{
                  ...inputStyle,

                  width: "100%",
                  height: BOX.inputH,

                  boxSizing: "border-box",

                  borderRadius: "7px",

                  fontSize: FS.sm,

                  border: "1px solid #D1D5DB",

                  background: "#FFFFFF",
                }}
              />
            </div>

            {/* =====================================================
                UNIT RELATIONSHIP
            ====================================================== */}

            <div
              style={{
                border: `1px solid ${THEME.border}`,

                borderRadius: "9px",

                background: "#FFFFFF",

                padding: PAD.sm,

                marginBottom: PAD.sm,
              }}
            >
              {/* ================= HEADER ================= */}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",

                  marginBottom: GAP.md,
                  gap: GAP.sm,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: GAP.sm,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: BOX.tileSm,
                      height: BOX.tileSm,
                      flexShrink: 0,

                      borderRadius: "7px",

                      background: THEME.light,

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <StraightenOutlined
                      style={{
                        fontSize: ICON.sm,
                        color: THEME.primary,
                      }}
                    />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: FS.sm,
                        fontWeight: 700,
                        color: THEME.text,
                      }}
                    >
                      Unit Relationship
                    </div>

                    {!isMobile && (
                      <div
                        style={{
                          fontSize: FS.xxs,
                          color: THEME.lightText,
                          marginTop: "2px",
                        }}
                      >
                        Define how the larger unit contains the smaller unit.
                      </div>
                    )}
                  </div>
                </div>

                <span
                  style={{
                    padding: `${GAP.xs} ${GAP.sm}`,

                    borderRadius: "12px",

                    background: THEME.light,

                    color: THEME.primary,

                    fontSize: FS.xxs,
                    fontWeight: 700,

                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  CONVERSION
                </span>
              </div>

              {/* =================================================
                  UNIT + CONVERSION
              ================================================== */}

              <div
                style={{
                  display: "grid",

                  gridTemplateColumns: unitGridColumns,

                  gap: GAP.md,

                  alignItems: "stretch",
                }}
              >
                {/* =================================================
                    LEFT SIDE — UNITS
                ================================================== */}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: GAP.sm,
                    minWidth: 0,
                  }}
                >
                  {/* UNIT 1 */}

                  <div style={{ minWidth: 0 }}>
                    <label
                      style={{
                        ...labelStyle,

                        fontSize: FS.xs,
                        fontWeight: 700,

                        marginBottom: "3px",
                      }}
                    >
                      Unit 1{" "}
                      <span
                        style={{
                          color: "#DC2626",
                        }}
                      >
                        *
                      </span>
                    </label>

                    <div
                      style={{
                        fontSize: FS.xxs,

                        color: THEME.primary,

                        fontWeight: 600,

                        marginBottom: "5px",
                      }}
                    >
                      Larger / Parent Unit
                    </div>

                    <input
                      type="text"
                      name="unit1"
                      value={formData.unit1}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="e.g. Box"
                      required
                      style={{
                        ...inputStyle,

                        width: "100%",
                        height: BOX.inputSm,

                        boxSizing: "border-box",

                        borderRadius: "7px",

                        fontSize: FS.sm,
                      }}
                    />
                  </div>

                  {/* UNIT 2 */}

                  <div style={{ minWidth: 0 }}>
                    <label
                      style={{
                        ...labelStyle,

                        fontSize: FS.xs,
                        fontWeight: 700,

                        marginBottom: "3px",
                      }}
                    >
                      Unit 2
                      <span
                        style={{
                          marginLeft: "6px",

                          fontSize: FS.xxs,

                          fontWeight: 500,

                          color: THEME.lightText,
                        }}
                      >
                        Optional
                      </span>
                    </label>

                    <div
                      style={{
                        fontSize: FS.xxs,

                        color: THEME.muted,

                        fontWeight: 600,

                        marginBottom: "5px",
                      }}
                    >
                      Smaller / Base Unit
                    </div>

                    <input
                      type="text"
                      name="unit2"
                      value={formData.unit2}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="e.g. Pcs"
                      style={{
                        ...inputStyle,

                        width: "100%",
                        height: BOX.inputSm,

                        boxSizing: "border-box",

                        borderRadius: "7px",

                        fontSize: FS.sm,
                      }}
                    />
                  </div>
                </div>

                {/* =================================================
                    RIGHT SIDE — CONVERSION
                ================================================== */}

                <div
                  style={{
                    border: "1px solid #E2E8F0",

                    borderRadius: "8px",

                    background: "#F8FAFC",

                    padding: PAD.sm,

                    display: "flex",
                    flexDirection: "column",

                    justifyContent: "center",

                    boxSizing: "border-box",

                    minWidth: 0,
                  }}
                >
                  {/* FORMULA */}

                  <div
                    style={{
                      display: "flex",

                      alignItems: "center",

                      justifyContent: "center",

                      flexWrap: "wrap",

                      gap: GAP.xs,

                      width: "100%",
                    }}
                  >
                    {/* UNIT 1 */}

                    <span
                      style={{
                        fontSize: FS.sm,

                        fontWeight: 700,

                        color: THEME.text,

                        whiteSpace: "nowrap",
                      }}
                    >
                      1 {formData.unit1 || "Unit 1"}
                    </span>

                    <span
                      style={{
                        fontSize: FS.sm,

                        fontWeight: 600,

                        color: THEME.lightText,
                      }}
                    >
                      =
                    </span>

                    {/* CONVERSION */}

                    <input
                      type="number"
                      name="conversionQty"
                      value={formData.unit2 ? formData.conversionQty : "1"}
                      onChange={handleChange}
                      min="1"
                      step={formData.decimal === "Yes" ? "0.01" : "1"}
                      disabled={!formData.unit2}
                      style={{
                        ...inputStyle,

                        width: "clamp(50px, 4.4vw, 68px)",
                        height: BOX.segH,

                        padding: "0 6px",

                        textAlign: "center",

                        fontSize: FS.sm,

                        fontWeight: 700,

                        boxSizing: "border-box",

                        borderRadius: "6px",

                        background: !formData.unit2 ? "#F1F5F9" : "#FFFFFF",

                        color: !formData.unit2 ? THEME.muted : THEME.text,

                        border: !formData.unit2
                          ? "1px solid #E2E8F0"
                          : `1px solid #C4B5FD`,

                        cursor: !formData.unit2 ? "not-allowed" : "text",
                      }}
                    />

                    {/* UNIT 2 */}

                    <span
                      style={{
                        fontSize: FS.sm,

                        fontWeight: 700,

                        color: THEME.primary,

                        whiteSpace: "nowrap",
                      }}
                    >
                      {formData.unit2 || formData.unit1 || "Unit 1"}
                    </span>
                  </div>

                  {/* PRIMARY UNIT */}

                  <div
                    style={{
                      marginTop: GAP.md,

                      paddingTop: GAP.sm,

                      borderTop: "1px solid #E2E8F0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: GAP.xs,

                        marginBottom: "3px",
                      }}
                    >
                      <CalculateOutlined
                        style={{
                          fontSize: ICON.xs,
                          color: THEME.accent,
                        }}
                      />

                      <div
                        style={{
                          fontSize: FS.xs,
                          fontWeight: 700,
                          color: THEME.text,
                        }}
                      >
                        Primary Unit
                      </div>
                    </div>

                    <div
                      style={{
                        fontSize: FS.xxs,
                        color: THEME.lightText,
                        marginBottom: GAP.xs,
                        lineHeight: 1.5,
                      }}
                    >
                      Select the unit used for inventory quantity.
                    </div>

                    {/* PRIMARY UNIT OPTIONS */}

                    <div
                      style={{
                        display: "grid",

                        gridTemplateColumns: "1fr 1fr",

                        gap: GAP.xs,
                      }}
                    >
                      {/* UNIT 1 */}

                      <label
                        style={{
                          minHeight: BOX.segH,

                          padding: `0 ${GAP.xs}`,

                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",

                          gap: GAP.xs,

                          border:
                            formData.primaryUnit === "unit1"
                              ? `1px solid ${THEME.primary}`
                              : "1px solid #D1D5DB",

                          borderRadius: "7px",

                          background:
                            formData.primaryUnit === "unit1"
                              ? THEME.light
                              : "#FFFFFF",

                          color:
                            formData.primaryUnit === "unit1"
                              ? THEME.primary
                              : THEME.muted,

                          fontSize: FS.xs,

                          fontWeight:
                            formData.primaryUnit === "unit1" ? 700 : 500,

                          cursor: "pointer",

                          transition: "all 0.15s ease",

                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <input
                          type="radio"
                          name="primaryUnit"
                          value="unit1"
                          checked={formData.primaryUnit === "unit1"}
                          onChange={handleChange}
                          style={{
                            margin: 0,

                            width: BOX.radio,
                            height: BOX.radio,

                            flexShrink: 0,

                            accentColor: THEME.primary,

                            cursor: "pointer",
                          }}
                        />

                        {formData.unit1 || "Unit 1"}
                      </label>

                      {/* UNIT 2 */}

                      {formData.unit2.trim() && (
                        <label
                          style={{
                            minHeight: BOX.segH,

                            padding: `0 ${GAP.xs}`,

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            gap: GAP.xs,

                            border:
                              formData.primaryUnit === "unit2"
                                ? `1px solid ${THEME.accent}`
                                : "1px solid #D1D5DB",

                            borderRadius: "7px",

                            background:
                              formData.primaryUnit === "unit2"
                                ? "#FAF9FF"
                                : "#FFFFFF",

                            color:
                              formData.primaryUnit === "unit2"
                                ? THEME.accent
                                : THEME.muted,

                            fontSize: FS.xs,

                            fontWeight:
                              formData.primaryUnit === "unit2" ? 700 : 500,

                            cursor: "pointer",

                            transition: "all 0.15s ease",

                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <input
                            type="radio"
                            name="primaryUnit"
                            value="unit2"
                            checked={formData.primaryUnit === "unit2"}
                            onChange={handleChange}
                            style={{
                              margin: 0,

                              width: BOX.radio,
                              height: BOX.radio,

                              flexShrink: 0,

                              accentColor: THEME.accent,

                              cursor: "pointer",
                            }}
                          />

                          {formData.unit2}
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* =====================================================
                ROW 2
            ====================================================== */}

            <div
              style={{
                display: "grid",

                gridTemplateColumns: row2Columns,

                gap: GAP.md,

                alignItems: "end",

                width: "100%",
              }}
            >
              {/* =================================================
                  TAX
              ================================================== */}

              <div
                style={{
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <label
                  style={{
                    ...labelStyle,

                    fontSize: FS.xs,
                    fontWeight: 700,

                    marginBottom: "5px",
                  }}
                >
                  Tax (%)
                </label>

                <div
                  style={{
                    position: "relative",
                    width: "100%",
                  }}
                >
                  <input
                    type="number"
                    name="tax"
                    value={formData.tax}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{
                      ...inputStyle,

                      width: "100%",

                      height: BOX.inputH,

                      boxSizing: "border-box",

                      paddingRight: "34px",

                      borderRadius: "7px",

                      fontSize: FS.sm,
                    }}
                  />

                  <PercentOutlined
                    style={{
                      position: "absolute",

                      right: "10px",

                      top: "50%",

                      transform: "translateY(-50%)",

                      color: THEME.accent,

                      fontSize: ICON.xs,

                      pointerEvents: "none",
                    }}
                  />
                </div>
              </div>

              {/* =================================================
                  ALLOW DECIMAL
              ================================================== */}

              <div
                style={{
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <label
                  style={{
                    ...labelStyle,

                    fontSize: FS.xs,
                    fontWeight: 700,

                    marginBottom: "5px",
                  }}
                >
                  Allow Decimal
                </label>

                <div
                  style={{
                    width: "100%",

                    height: BOX.inputH,

                    display: "grid",

                    gridTemplateColumns: "1fr 1fr",

                    border: "1px solid #D1D5DB",

                    borderRadius: "7px",

                    overflow: "hidden",

                    background: "#FFFFFF",

                    boxSizing: "border-box",
                  }}
                >
                  {["Yes", "No"].map((option, index) => {
                    const isSelected = formData.decimal === option;

                    return (
                      <label
                        key={option}
                        style={{
                          position: "relative",

                          display: "flex",

                          alignItems: "center",

                          justifyContent: "center",

                          gap: GAP.xs,

                          height: "100%",

                          borderRight:
                            index === 0 ? "1px solid #D1D5DB" : "none",

                          background: isSelected ? THEME.light : "#FFFFFF",

                          color: isSelected ? THEME.primary : THEME.muted,

                          fontSize: FS.sm,

                          fontWeight: isSelected ? 700 : 500,

                          cursor: "pointer",

                          transition: "all 0.15s ease",
                        }}
                      >
                        <input
                          type="radio"
                          name="decimal"
                          value={option}
                          checked={formData.decimal === option}
                          onChange={handleChange}
                          style={{
                            width: BOX.radio,

                            height: BOX.radio,

                            margin: 0,

                            flexShrink: 0,

                            accentColor: THEME.primary,

                            cursor: "pointer",
                          }}
                        />

                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* =================================================
                  ACTION BUTTONS
              ================================================== */}

              <div
                style={{
                  minWidth: 0,
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: GAP.sm,
                  alignItems: "end",
                  gridColumn: isNarrow && !isMobile ? "1 / -1" : "auto",
                }}
              >
                {/* RESET */}
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving}
                  style={{
                    width: "100%",
                    height: BOX.btnH,
                    padding: `0 ${GAP.sm}`,
                    boxSizing: "border-box",
                    borderRadius: "7px",
                    border: "1px solid #D1D5DB",
                    background: "#FFFFFF",
                    fontSize: FS.sm,
                    fontWeight: 600,
                    color: THEME.muted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: GAP.xs,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <RestartAltOutlined
                    style={{
                      fontSize: ICON.xs,
                    }}
                  />
                  Reset
                </button>

                {/* SAVE */}

                <button
                  type="submit"
                  style={{
                    width: "100%",
                    height: BOX.btnH,
                    padding: `0 ${GAP.sm}`,
                    boxSizing: "border-box",
                    borderRadius: "7px",
                    border: "none",
                    background: "linear-gradient(135deg, #6D28D9, #8B5CF6)",
                    color: "#FFFFFF",
                    fontSize: FS.sm,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: GAP.xs,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    boxShadow: "0 3px 9px rgba(109,40,217,0.22)",
                    transition: "all 0.15s ease",
                  }}
                >
                  <SaveOutlined
                    style={{
                      fontSize: ICON.xs,
                    }}
                  />
                  {saving ? "Saving..." : "Save Product"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddProduct;
